/**
 * API Usage Dashboard
 *
 * Comprehensive dashboard showing ALL API usage across the platform,
 * categorized by request type (crisis, essays, strategy, etc.)
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Users,
  BarChart3,
  FileText,
  Brain,
  BookOpen,
  HelpCircle,
  Clock,
  RefreshCw,
  Download,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { UserQuotaManager } from '../../services/api/UserQuotaManager';

const APIUsageDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('daily');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(60000); // 1 minute

  const quotaManager = new UserQuotaManager();

  useEffect(() => {
    loadAnalytics();
    const interval = setInterval(loadAnalytics, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await quotaManager.getSystemWideAnalytics();
      setAnalytics(data);
      setLoading(false);
    } catch (error) {
      console.error('[Dashboard] Failed to load analytics:', error);
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      crisis: <AlertTriangle className="w-5 h-5 text-red-500" />,
      essay: <FileText className="w-5 h-5 text-blue-500" />,
      strategy: <Brain className="w-5 h-5 text-violet-500" />,
      academic: <BookOpen className="w-5 h-5 text-green-500" />,
      general: <HelpCircle className="w-5 h-5 text-gray-500" />,
      other: <Activity className="w-5 h-5 text-gray-400" />
    };
    return icons[category] || icons.other;
  };

  const getCategoryDescription = (category) => {
    const descriptions = {
      crisis: 'Emergency interventions, recovery plans, workload optimization',
      essay: 'Essay reviews, brainstorming, feedback, writing improvement',
      strategy: 'College selection, application strategy, timeline planning',
      academic: 'Test prep, course planning, grade improvement strategies',
      general: 'General advice, task batching, scheduling help',
      other: 'Miscellaneous requests'
    };
    return descriptions[category] || '';
  };

  const exportAnalytics = () => {
    const csv = generateCSV(analytics);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api-usage-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const generateCSV = (data) => {
    let csv = 'Category,Daily Usage,Monthly Usage,Cost\n';

    // By model
    csv += `\nBy Model\n`;
    csv += `Opus,${data.totalRequests.opus.daily},${data.totalRequests.opus.monthly},${(data.totalRequests.opus.daily * 0.105).toFixed(2)}\n`;
    csv += `Sonnet,${data.totalRequests.sonnet.daily},${data.totalRequests.sonnet.monthly},${(data.totalRequests.sonnet.daily * 0.021).toFixed(2)}\n`;
    csv += `Haiku,${data.totalRequests.haiku.daily},${data.totalRequests.haiku.monthly},${(data.totalRequests.haiku.daily * 0.002).toFixed(2)}\n`;

    // By category
    csv += `\nBy Category\n`;
    for (const [category, counts] of Object.entries(data.byCategory)) {
      csv += `${category},${counts.daily},${counts.monthly}\n`;
    }

    return csv;
  };

  if (loading && !analytics) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-violet-600" />
            API Usage Analytics
          </h1>
          <p className="text-gray-600 mt-1">
            Comprehensive view of ALL API usage across the platform
          </p>
        </div>

        <div className="flex items-center gap-4">
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            className="px-3 py-2 border rounded-lg"
          >
            <option value={30000}>Refresh: 30s</option>
            <option value={60000}>Refresh: 1m</option>
            <option value={300000}>Refresh: 5m</option>
            <option value={0}>Manual only</option>
          </select>

          <button
            onClick={loadAnalytics}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={exportAnalytics}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Top Level Metrics */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Daily Cost"
          value={`$${analytics.dailyCost}`}
          subtitle={`Projected Monthly: $${analytics.projectedMonthlyCost}`}
          icon={<DollarSign className="w-6 h-6 text-green-600" />}
          trend={analytics.dailyCost > 50 ? 'high' : 'normal'}
        />

        <MetricCard
          title="Active Users"
          value={analytics.totalUsers}
          subtitle="With API usage today"
          icon={<Users className="w-6 h-6 text-blue-600" />}
        />

        <MetricCard
          title="Total Requests"
          value={
            analytics.totalRequests.opus.daily +
            analytics.totalRequests.sonnet.daily +
            analytics.totalRequests.haiku.daily
          }
          subtitle="Across all models today"
          icon={<Activity className="w-6 h-6 text-violet-600" />}
        />

        <MetricCard
          title="Quota Exhausted"
          value={analytics.quotaExhausted.opus + analytics.quotaExhausted.sonnet}
          subtitle="Users hitting limits"
          icon={<AlertTriangle className="w-6 h-6 text-orange-600" />}
          trend={analytics.quotaExhausted.opus > 5 ? 'high' : 'normal'}
        />
      </div>

      {/* Usage by Model */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Usage by Model</h2>

        <div className="space-y-4">
          <ModelUsageBar
            model="Opus"
            daily={analytics.totalRequests.opus.daily}
            monthly={analytics.totalRequests.opus.monthly}
            cost={analytics.totalRequests.opus.daily * 0.105}
            color="violet"
            description="Highest accuracy - Crisis interventions"
          />

          <ModelUsageBar
            model="Sonnet"
            daily={analytics.totalRequests.sonnet.daily}
            monthly={analytics.totalRequests.sonnet.monthly}
            cost={analytics.totalRequests.sonnet.daily * 0.021}
            color="blue"
            description="Balanced - Complex tasks"
          />

          <ModelUsageBar
            model="Haiku"
            daily={analytics.totalRequests.haiku.daily}
            monthly={analytics.totalRequests.haiku.monthly}
            cost={analytics.totalRequests.haiku.daily * 0.002}
            color="green"
            description="Fast & cheap - Simple tasks"
          />
        </div>

        <div className="mt-6 pt-6 border-t">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total API Calls Today</span>
            <span className="font-bold text-lg">
              {analytics.totalRequests.opus.daily +
               analytics.totalRequests.sonnet.daily +
               analytics.totalRequests.haiku.daily}
            </span>
          </div>
        </div>
      </div>

      {/* Usage by Category - The key insight! */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">
          Usage by Request Type
          <span className="text-sm font-normal text-gray-600 ml-2">
            (What users are actually using AI for)
          </span>
        </h2>

        <div className="space-y-3">
          {Object.entries(analytics.byCategory).map(([category, counts]) => (
            <CategoryUsageRow
              key={category}
              category={category}
              icon={getCategoryIcon(category)}
              daily={counts.daily}
              monthly={counts.monthly}
              description={getCategoryDescription(category)}
              expanded={expandedCategory === category}
              onToggle={() => setExpandedCategory(
                expandedCategory === category ? null : category
              )}
            />
          ))}
        </div>

        {/* Insights */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">Usage Insights</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>
              • Crisis interventions: {Math.round(
                (analytics.byCategory.crisis.daily /
                 Object.values(analytics.byCategory).reduce((sum, c) => sum + c.daily, 0)) * 100
              )}% of daily usage
            </li>
            <li>
              • Essay help: {Math.round(
                (analytics.byCategory.essay.daily /
                 Object.values(analytics.byCategory).reduce((sum, c) => sum + c.daily, 0)) * 100
              )}% of daily usage
            </li>
            <li>
              • Most active category: {
                Object.entries(analytics.byCategory)
                  .sort((a, b) => b[1].daily - a[1].daily)[0][0]
              }
            </li>
          </ul>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-xl font-semibold mb-4">Cost Analysis</h2>

        <div className="grid grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              ${analytics.hourlyCost.toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">Per Hour</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              ${analytics.dailyCost.toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">Today</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-violet-600">
              ${analytics.projectedMonthlyCost.toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">Projected Monthly</div>
          </div>
        </div>

        {/* Alert if costs are high */}
        {analytics.dailyCost > 100 && (
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center gap-2 text-orange-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">High usage alert</span>
            </div>
            <p className="text-sm text-orange-700 mt-1">
              Daily costs exceeding $100. Consider reviewing quota limits.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Sub-component for metric cards
const MetricCard = ({ title, value, subtitle, icon, trend }) => (
  <div className="bg-white rounded-xl shadow-sm border p-6">
    <div className="flex items-start justify-between mb-2">
      <div className="p-2 bg-gray-50 rounded-lg">{icon}</div>
      {trend === 'high' && (
        <TrendingUp className="w-5 h-5 text-orange-500" />
      )}
    </div>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-sm text-gray-600 mt-1">{subtitle}</div>
  </div>
);

// Sub-component for model usage bars
const ModelUsageBar = ({ model, daily, monthly, cost, color, description }) => {
  const maxDaily = 500; // Adjust based on your scale
  const percentage = Math.min(100, (daily / maxDaily) * 100);

  const colorClasses = {
    violet: 'bg-violet-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500'
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium">{model}</span>
          <span className="text-sm text-gray-600 ml-2">{description}</span>
        </div>
        <div className="text-right">
          <span className="font-bold">{daily}</span>
          <span className="text-sm text-gray-600"> today</span>
          <span className="text-sm text-gray-500 ml-2">(${cost.toFixed(2)})</span>
        </div>
      </div>

      <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          className={`h-full ${colorClasses[color]}`}
        />
      </div>

      <div className="text-xs text-gray-500">
        Monthly: {monthly} requests
      </div>
    </div>
  );
};

// Sub-component for category usage rows
const CategoryUsageRow = ({
  category,
  icon,
  daily,
  monthly,
  description,
  expanded,
  onToggle
}) => (
  <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
    <div
      className="flex items-center justify-between cursor-pointer"
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <span className="font-medium capitalize">{category}</span>
          <div className="text-xs text-gray-500 mt-0.5">{description}</div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="text-right">
          <span className="font-bold">{daily}</span>
          <span className="text-sm text-gray-600"> today</span>
        </div>
        {expanded ? <ChevronUp /> : <ChevronDown />}
      </div>
    </div>

    {expanded && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        className="mt-4 pt-4 border-t"
      >
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Daily requests:</span>
            <span className="font-medium ml-2">{daily}</span>
          </div>
          <div>
            <span className="text-gray-600">Monthly requests:</span>
            <span className="font-medium ml-2">{monthly}</span>
          </div>
        </div>

        {/* Add more detailed breakdown here if needed */}
        <div className="mt-3 text-xs text-gray-500">
          Common request types in this category:
          <ul className="mt-1 ml-4">
            {category === 'crisis' && (
              <>
                <li>• Recovery plans for overwhelmed students</li>
                <li>• Emergency deadline management</li>
                <li>• Workload optimization</li>
              </>
            )}
            {category === 'essay' && (
              <>
                <li>• Essay reviews and feedback</li>
                <li>• Brainstorming and ideation</li>
                <li>• Writing style improvements</li>
              </>
            )}
            {category === 'strategy' && (
              <>
                <li>• College list optimization</li>
                <li>• Application timeline planning</li>
                <li>• Strategic positioning advice</li>
              </>
            )}
          </ul>
        </div>
      </motion.div>
    )}
  </div>
);

export default APIUsageDashboard;